import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { getMe, signOut as signOutFn } from '@generated/server/auth';

export type SessionUser = Awaited<ReturnType<typeof getMe>>['user'];

interface Ctx {
  sid: string;
  user: SessionUser | null;
  ready: boolean;
  setSession: (sid: string, user: SessionUser) => void;
  signOut: () => Promise<void>;
  isStaff: boolean;
}

const SessionCtx = createContext<Ctx | null>(null);
const KEY = 'sdl_sid';

export function SessionProvider({ children }: { children: ReactNode }) {
  const [sid, setSid] = useState('');
  const [user, setUser] = useState<SessionUser | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(KEY) ?? '';
    if (!stored) { setReady(true); return; }
    getMe({ data: { sid: stored } })
      .then((r) => { setSid(stored); setUser(r?.user ?? null); })
      .catch(() => localStorage.removeItem(KEY))
      .finally(() => setReady(true));
  }, []);

  const setSession = useCallback((s: string, u: SessionUser) => {
    localStorage.setItem(KEY, s);
    setSid(s);
    setUser(u);
  }, []);

  const signOut = useCallback(async () => {
    const s = sid;
    localStorage.removeItem(KEY);
    setSid('');
    setUser(null);
    try { await signOutFn({ data: { sid: s } }); } catch (e) { console.error(e); }
  }, [sid]);

  // 30-minute idle timeout on the client as well
  useEffect(() => {
    if (!user) return;
    let last = Date.now();
    const bump = () => { last = Date.now(); };
    const events = ['mousemove', 'keydown', 'click', 'scroll'];
    events.forEach((e) => window.addEventListener(e, bump));
    const t = window.setInterval(() => { if (Date.now() - last > 30 * 60 * 1000) void signOut(); }, 30000);
    return () => { events.forEach((e) => window.removeEventListener(e, bump)); window.clearInterval(t); };
  }, [user, signOut]);

  const isStaff = !!user && ['admin', 'case_manager', 'reviewer'].includes(user.role);
  return <SessionCtx.Provider value={{ sid, user, ready, setSession, signOut, isStaff }}>{children}</SessionCtx.Provider>;
}

export function useSession(): Ctx {
  const c = useContext(SessionCtx);
  if (!c) throw new Error('SessionProvider missing');
  return c;
}

export function useAuthedUser(): SessionUser {
  const { user } = useSession();
  if (!user) throw new Error('Not signed in');
  return user;
}

export const ROLE_LABEL: Record<string, string> = { admin: 'Administrator', case_manager: 'Case Manager', reviewer: 'Reviewer', adjudicator: 'Adjudicator', client: 'Client' };
